import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ACCOUNT_LINKS = [
  { to: "/alertas" as const, label: "Alertas" },
  { to: "/favoritos" as const, label: "Favoritos" },
  { to: "/minha-conta" as const, label: "Conta" },
];

const LINK_CLASS =
  "rounded-full px-4 py-2 text-xs font-medium text-foreground/70 transition dark:hover:bg-white/10 hover:bg-black/10 hover:text-foreground/90";

const MOBILE_LINK_CLASS =
  "rounded-xl px-4 py-4 text-base font-medium text-foreground/70 transition dark:hover:bg-white/10 hover:bg-black/10 hover:text-foreground/90";

export function AppNav({ maxWidth = "max-w-6xl" }: { maxWidth?: string }) {
  const { session } = useSession();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const signOut = async () => {
    setMenuOpen(false);
    setAccountOpen(false);
    await supabase.auth.signOut();
    navigate({ to: "/" });
  };

  const close = () => setMenuOpen(false);

  return (
    <>
      <nav className={`relative mx-auto flex ${maxWidth} items-center justify-between px-6 py-6`}>
        <Link to="/" className="text-foreground" aria-label="Busca Blindados">
          <Logo className="h-6 w-auto md:h-10" />
        </Link>

        {/* Desktop: same shape on every page — Blog + one account slot. */}
        <div className="hidden items-center gap-2 md:flex">
          <Link to="/blog" className={LINK_CLASS}>
            Blog
          </Link>
          {!session ? (
            <Link
              to="/login"
              className="chip rounded-full px-5 py-2.5 text-sm font-medium text-foreground/90 transition dark:hover:bg-white/10 hover:bg-black/10"
            >
              Entrar
            </Link>
          ) : (
            <Popover open={accountOpen} onOpenChange={setAccountOpen}>
              <PopoverTrigger asChild>
                <button
                  className="chip flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium text-foreground/90 transition dark:hover:bg-white/10 hover:bg-black/10"
                >
                  Minha conta
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1">
                {ACCOUNT_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setAccountOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm text-foreground/80 transition dark:hover:bg-white/10 hover:bg-black/10 hover:text-foreground"
                  >
                    {l.label}
                  </Link>
                ))}
                <button
                  onClick={signOut}
                  className="block w-full rounded-lg px-3 py-2 text-left text-sm text-foreground/80 transition dark:hover:bg-white/10 hover:bg-black/10 hover:text-foreground"
                >
                  Sair
                </button>
              </PopoverContent>
            </Popover>
          )}
          <ThemeToggle />
        </div>

        {/* Mobile: ThemeToggle + burger, logged in or not. */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center justify-center rounded-lg p-2 text-foreground/70 transition dark:hover:bg-white/10 hover:bg-black/10 hover:text-foreground"
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background md:hidden">
          <div className="flex items-center justify-between px-6 py-6">
            <Link to="/" onClick={close} className="text-foreground" aria-label="Busca Blindados">
              <Logo className="h-6 w-auto" />
            </Link>
            <button
              onClick={close}
              className="flex items-center justify-center rounded-lg p-2 text-foreground/70 transition dark:hover:bg-white/10 hover:bg-black/10 hover:text-foreground"
              aria-label="Fechar menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex flex-1 flex-col gap-1 px-4 pt-4">
            <Link to="/blog" onClick={close} className={MOBILE_LINK_CLASS}>
              Blog
            </Link>
            {!session ? (
              <Link to="/login" onClick={close} className={MOBILE_LINK_CLASS}>
                Entrar
              </Link>
            ) : (
              <>
                {ACCOUNT_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={close} className={MOBILE_LINK_CLASS}>
                    {l.label}
                  </Link>
                ))}
                <button onClick={signOut} className={`mt-auto text-left ${MOBILE_LINK_CLASS}`}>
                  Sair
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </>
  );
}
